import {
  IApplicationFormData,
  IWizardCoApplicant,
  normalizeApplicationFormData,
} from '../../models/application-form'

export const isCoApplicantApplication = (data?: Partial<IApplicationFormData>): boolean =>
  (data?.renterRole ?? '').trim().toLowerCase() === 'co-applicant'

export const IsInviteApplication = (data?: Partial<IApplicationFormData>): boolean =>
  isCoApplicantApplication(data)

export const getCoApplicantInvite = (
  primaryApplicant: Partial<IApplicationFormData> | null | undefined,
  inviteId: string | null | undefined
): IWizardCoApplicant | undefined => {
  const normalizedInviteId = inviteId?.trim()
  if (!normalizedInviteId) {
    return undefined
  }

  return primaryApplicant?.coApplicants?.find(
    (coApplicant) => coApplicant.inviteId?.trim() === normalizedInviteId
  )
}

export const getPrimaryApplicantName = (
  primaryApplicant: Partial<IApplicationFormData> | null | undefined
): string => {
  const fullName = primaryApplicant?.fullName?.trim()
  if (fullName) {
    return fullName
  }

  return [primaryApplicant?.firstName, primaryApplicant?.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ')
}

export const getFirstName = (name?: string): string => {
  const trimmed = name?.trim()
  if (!trimmed) {
    return ''
  }

  return trimmed.split(/\s+/)[0]
}

export const buildCoApplicantInviteInitialData = (
  primaryApplicant: Partial<IApplicationFormData>,
  inviteId: string
): Partial<IApplicationFormData> => {
  const invite = getCoApplicantInvite(primaryApplicant, inviteId)
  const fullName = invite?.fullName?.trim() ?? ''
  const nameParts = splitFullName(fullName)
  const primaryApplicantName = getPrimaryApplicantName(primaryApplicant)

  return normalizeApplicationFormData({
    renterRole: 'co-applicant',
    coApplicantInviteId: inviteId,
    primaryApplicantName,
    firstName: nameParts.firstName,
    lastName: nameParts.lastName,
    mobileNumber: invite?.mobileNumber,
    email: invite?.email,
    applicationType: primaryApplicant.applicationType,
    moveInAsap: primaryApplicant.moveInAsap,
    moveInPreferredDate: primaryApplicant.moveInPreferredDate,
    preferredLeaseTerm: primaryApplicant.preferredLeaseTerm,
    rentWeeklyBudgetMin: primaryApplicant.rentWeeklyBudgetMin,
    rentWeeklyBudgetMax: primaryApplicant.rentWeeklyBudgetMax,
    retentionPreference: primaryApplicant.retentionPreference,
    termsAccepted: false,
    consentSharingAccepted: false,
    isMobileVerified: false,
  })
}

export const getFullName = (firstName?: string, lastName?: string): string => {
  return [firstName, lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ')
}

const splitFullName = (fullName?: string): {firstName?: string; lastName?: string} => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return {}
  }

  if (parts.length === 1) {
    return {firstName: parts[0], lastName: ''}
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  }
}
